import { keyframes } from '@emotion/core';
import { promisify } from '../utils';

const loadStyles = css => {
  const head = document.head || document.getElementsByTagName('head')[0]; // no <head> node? create one...

  if (!head) {
    head = document.createElement('head');
    const body = document.body || document.getElementsByTagName('body')[0];

    if (body) {
      body.parentNode.insertBefore(head, body);
    } else {
      document.documentElement.appendChild(head);
    }
  }

  const style = document.createElement('style');

  if (style.styleSheet) {
    // IE
    style.styleSheet.cssText = css;
  } else {
    // the world
    style.appendChild(document.createTextNode(css));
  }

  head.appendChild(style);
  return style;
};

const pfx = ['webkit', 'moz', 'MS', 'o', ''];

const addStyleEvent = (el, type, callback = () => {}) => {
  for (let p in pfx) {
    if (!pfx[p]) {
      type = type.toLowerCase();
    }

    el.addEventListener(pfx[p] + type, callback, false);
  }
};

const removeStyleEvent = (el, type, callback = () => {}) => {
  for (let p in pfx) {
    if (!pfx[p]) {
      type = type.toLowerCase();
    }

    el.removeEventListener(pfx[p] + type, callback, false);
  }
};

const getTransformProperty = () => {
  const styles = ['webkitTransform', 'MozTransform', 'msTransform', 'OTransform', 'transform'];
  const el = document.createElement('p');
  let style;

  for (let i in styles) {
    style = styles[i];

    if (el.style[style] !== null) {
      return style;
    }
  }
};

const getAnimationProperty = () => {
  const styles = ['webkitAnimation', 'MozAnimation', 'msAnimation', 'OAnimation', 'animation'];
  const el = document.createElement('div');
  let style;

  for (let i in styles) {
    style = styles[i];

    if (el.style[style] !== null) {
      return style;
    }
  }
};

const registeredAnimations = {};
const mainDefaults = {
  duration: 1000,
  fillMode: 'both',
  easing: 'ease',
  iterations: 1,
  delay: 0,
  direction: 'normal',
  resetWhenDone: false,
  clearTransformsBeforeStart: false
};
export const hasAnimation = name => {
  return registeredAnimations.hasOwnProperty(name);
};

const clearTransformProp = el => {
  el.style.transform = '';
  el.style[getTransformProperty()] = '';
};

const clearAnimationProp = el => {
  el.style[getAnimationProperty()] = '';
  el.style.animation = '';
};

const setAnimationProp = (els, val, opts) => {
  const clearTransforms = opts && opts.clearTransforms === true; // we got a collection, potentially

  for (let i in els) {
    // we do both because... chrome does both
    if (clearTransforms) {
      clearTransformProp(els[i]);
    }

    if (els[i].style) {
      els[i].style[getAnimationProperty()] = val;
    }
  }
};

const addUnit = (item, unit) => {
  if (typeof item === 'string') {
    return item;
  }

  return item + unit;
};

const getValueForProperty = (val, prop) => {
  if (val.hasOwnProperty(prop)) {
    return val[prop];
  }

  return 0;
};

const createCssTranslateString = val => {
  var res = []; // array of values is assumed to be [x,y,(z)]

  if (Array.isArray(val)) {
    if (val.length === 2) {
      res = res.concat(val, 0);
    } else {
      res = val;
    }
  } else {
    res.push(getValueForProperty(val, 'x'));
    res.push(getValueForProperty(val, 'y'));
    res.push(getValueForProperty(val, 'z'));
  }

  res = res.map(function (val) {
    return addUnit(val, 'px');
  });
  return 'translate3d(' + res.join(',') + ')';
};

const lookups = {
  scale: val => {
    return 'scale(' + val + ')';
  },
  rotate: val => {
    return 'rotate(' + addUnit(val, 'deg') + ')';
  },
  translate: val => {
    return createCssTranslateString(val);
  },
  transform: val => {
    return val;
  }
};

const prefix = () => {
  if (typeof document !== 'undefined') {
    const el = document.documentElement || document.createElement('div');
    return el.style.animation !== null ? '' : '-webkit-';
  }
};

const getStyles = obj => {
  // array of values is assumed to be [x,y,(z)]
  if (Array.isArray(obj)) {
    return prefix() + 'transform:' + createCssTranslateString(obj);
  }

  let transformBuff = [],
      otherStyles = []; // object assumes

  if (typeof obj === 'object') {
    for (let key in obj) {
      if (lookups[key]) {
        transformBuff.push(lookups[key](obj[key]));
      } else {
        otherStyles.push(key + ':' + obj[key]);
      }
    }
  }

  otherStyles.push(prefix() + 'transform:' + transformBuff.join(''));
  return otherStyles.join(';');
};

const getLine = (percentage, styles) => {
  if (typeof percentage === 'number') {
    percentage += '%';
  } else if (typeof percentage === 'string' && percentage.indexOf('%') === -1) {
    percentage += '%';
  }

  return percentage + '{' + getStyles(styles) + '}';
};

const createKeyframeAnimation = (name, positions) => {
  let buf = '@' + prefix() + 'keyframes ' + name + '{'; // we got an array of arrays

  if (Array.isArray(positions)) {
    buf += positions.map(function (pos, index) {
      let percentage;

      if (index) {
        percentage = index / (positions.length - 1) * 100;
      } else {
        percentage = 0;
      }

      return getLine(percentage, pos);
    }).join('');
  } else {
    for (let key in positions) {
      buf += getLine(key, positions[key]);
    }
  }

  buf += '}';
  return buf;
};

const setAnimationAsTransform = (els, opts) => {
  const clearAnimations = opts && opts.clearAnimations === true; // we got a collection, potentially

  for (let i in els) {
    els[i].style[getTransformProperty()] = getComputedStyle(els[i])[getTransformProperty()];
    if (clearAnimations) clearAnimationProp(els[i]);
  }
};

export const runAnimation = promisify((els, opts, cb = () => {}) => {
  if (typeof opts === 'string') {
    opts = {
      name: opts
    };
  }

  if (!els.length) {
    els = [els];
  }

  if (!opts.name) {
    return cb(Error('must supply animation name'));
  }

  const found = registeredAnimations[opts.name];

  if (!found) {
    return cb(Error(`no animation named ${opts.name} exists`));
  }

  opts = { ...opts,
    ...found.presets,
    ...mainDefaults
  };

  const animationEnd = () => {
    removeStyleEvent(els[0], 'AnimationEnd', animationEnd);

    if (opts.resetWhenDone) {
      setAnimationAsTransform(els, {
        clearAnimations: true
      });
    }

    return cb(null, els);
  };

  addStyleEvent(els[0], 'AnimationEnd', animationEnd);
  const styles = [opts.name, addUnit(opts.duration, 'ms'), opts.easing, opts.iterations, addUnit(opts.delay, 'ms'), opts.direction, opts.fillMode];
  setAnimationProp(els, styles.join(' '), {
    clearTransforms: opts.clearTransformsBeforeStart
  });
});
export const registerAnimation = opts => {
  unregisterAnimation(opts.name);
  const el = loadStyles(createKeyframeAnimation(opts.name, opts.animation));
  el.setAttribute('data-name', opts.name);
  registeredAnimations[opts.name] = {
    el: el,
    presets: opts.presets || {}
  };
};
export const unregisterAnimation = name => {
  if (hasAnimation(name)) {
    const styleEl = registeredAnimations[name].el;
    styleEl.parentNode.removeChild(styleEl);
    delete registeredAnimations[name];
  }
};
const backInDown = keyframes`
	0% {
		transform: translateY(-1200px) scale(0.7);
		opacity: 0.7;
	}

	80% {
		transform: translateY(0px) scale(0.7);
		opacity: 0.7;
	}

	100% {
		transform: scale(1);
		opacity: 1;
	}
`;
const backInLeft = keyframes`
	0% {
		transform: translateX(-2000px) scale(0.7);
		opacity: 0.7;
	}

	80% {
		transform: translateX(0px) scale(0.7);
		opacity: 0.7;
	}

	100% {
		transform: scale(1);
		opacity: 1;
	}
`;
const backInRight = keyframes`
	0% {
		transform: translateX(2000px) scale(0.7);
		opacity: 0.7;
	}

	80% {
		transform: translateX(0px) scale(0.7);
		opacity: 0.7;
	}

	100% {
		transform: scale(1);
		opacity: 1;
	}
`;
const backInUp = keyframes`
	0% {
		transform: translateY(1200px) scale(0.7);
		opacity: 0.7;
	}

	80% {
		transform: translateY(0px) scale(0.7);
		opacity: 0.7;
	}

	100% {
		transform: scale(1);
		opacity: 1;
	}
`;
const backOutDown = keyframes`
	0% {
		transform: scale(1);
		opacity: 1;
	}

	20% {
		transform: translateY(0px) scale(0.7);
		opacity: 0.7;
	}

	100% {
		transform: translateY(700px) scale(0.7);
		opacity: 0.7;
	}
`;
const backOutLeft = keyframes`
	0% {
		transform: scale(1);
		opacity: 1;
	}

	20% {
		transform: translateX(0px) scale(0.7);
		opacity: 0.7;
	}

	100% {
		transform: translateX(-2000px) scale(0.7);
		opacity: 0.7;
	}
`;
const backOutRight = keyframes`
	0% {
		transform: scale(1);
		opacity: 1;
	}

	20% {
		transform: translateX(0px) scale(0.7);
		opacity: 0.7;
	}

	100% {
		transform: translateX(2000px) scale(0.7);
		opacity: 0.7;
	}
`;
const backOutUp = keyframes`
	0% {
		transform: scale(1);
		opacity: 1;
	}

	20% {
		transform: translateY(0px) scale(0.7);
		opacity: 0.7;
	}

	100% {
		transform: translateY(-700px) scale(0.7);
		opacity: 0.7;
	}
`;
const bounceIn = keyframes`
	from,
	20%,
	40%,
	60%,
	80%,
	to {
		animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);
	}

	0% {
		opacity: 0;
		transform: scale3d(0.3, 0.3, 0.3);
	}

	20% {
		transform: scale3d(1.1, 1.1, 1.1);
	}

	40% {
		transform: scale3d(0.9, 0.9, 0.9);
	}

	60% {
		opacity: 1;
		transform: scale3d(1.03, 1.03, 1.03);
	}

	80% {
		transform: scale3d(0.97, 0.97, 0.97);
	}

	to {
		opacity: 1;
		transform: scale3d(1, 1, 1);
	}
`;
const bounceInDown = keyframes`
	from,
	60%,
	75%,
	90%,
	to {
		animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);
	}

	0% {
		opacity: 0;
		transform: translate3d(0, -3000px, 0) scaleY(3);
	}

	60% {
		opacity: 1;
		transform: translate3d(0, 25px, 0) scaleY(0.9);
	}

	75% {
		transform: translate3d(0, -10px, 0) scaleY(0.95);
	}

	90% {
		transform: translate3d(0, 5px, 0) scaleY(0.985);
	}

	to {
		transform: translate3d(0, 0, 0);
	}
`;
const bounceInLeft = keyframes`
	from,
	60%,
	75%,
	90%,
	to {
		animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);
	}

	0% {
		opacity: 0;
		transform: translate3d(-3000px, 0, 0) scaleX(3);
	}

	60% {
		opacity: 1;
		transform: translate3d(25px, 0, 0) scaleX(1);
	}

	75% {
		transform: translate3d(-10px, 0, 0) scaleX(0.98);
	}

	90% {
		transform: translate3d(5px, 0, 0) scaleX(0.995);
	}

	to {
		transform: translate3d(0, 0, 0);
	}
`;
const bounceInRight = keyframes`
	from,
	60%,
	75%,
	90%,
	to {
		animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);
	}

	from {
		opacity: 0;
		transform: translate3d(3000px, 0, 0) scaleX(3);
	}

	60% {
		opacity: 1;
		transform: translate3d(-25px, 0, 0) scaleX(1);
	}

	75% {
		transform: translate3d(10px, 0, 0) scaleX(0.98);
	}

	90% {
		transform: translate3d(-5px, 0, 0) scaleX(0.995);
	}

	to {
		transform: translate3d(0, 0, 0);
	}
`;
const bounceInUp = keyframes`
	from,
	60%,
	75%,
	90%,
	to {
		animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);
	}

	from {
		opacity: 0;
		transform: translate3d(0, 3000px, 0) scaleY(5);
	}

	60% {
		opacity: 1;
		transform: translate3d(0, -20px, 0) scaleY(0.9);
	}

	75% {
		transform: translate3d(0, 10px, 0) scaleY(0.95);
	}

	90% {
		transform: translate3d(0, -5px, 0) scaleY(0.985);
	}

	to {
		transform: translate3d(0, 0, 0);
	}
`;
const bounceOut = keyframes`
	20% {
		transform: scale3d(0.9, 0.9, 0.9);
	}

	50%,
	55% {
		opacity: 1;
		transform: scale3d(1.1, 1.1, 1.1);
	}

	to {
		opacity: 0;
		transform: scale3d(0.3, 0.3, 0.3);
	}
`;
const bounceOutDown = keyframes`
	20% {
		transform: translate3d(0, 10px, 0) scaleY(0.985);
	}

	40%,
	45% {
		opacity: 1;
		transform: translate3d(0, -20px, 0) scaleY(0.9);
	}

	to {
		opacity: 0;
		transform: translate3d(0, 2000px, 0) scaleY(3);
	}
`;
const bounceOutLeft = keyframes`
	20% {
		opacity: 1;
		transform: translate3d(20px, 0, 0) scaleX(0.9);
	}

	to {
		opacity: 0;
		transform: translate3d(-2000px, 0, 0) scaleX(2);
	}
`;
const bounceOutRight = keyframes`
	20% {
		opacity: 1;
		transform: translate3d(-20px, 0, 0) scaleX(0.9);
	}

	to {
		opacity: 0;
		transform: translate3d(2000px, 0, 0) scaleX(2);
	}
`;
const bounceOutUp = keyframes`
	20% {
		transform: translate3d(0, -10px, 0) scaleY(0.985);
	}

	40%,
	45% {
		opacity: 1;
		transform: translate3d(0, 20px, 0) scaleY(0.9);
	}

	to {
		opacity: 0;
		transform: translate3d(0, -2000px, 0) scaleY(3);
	}
`;
const fadeIn = keyframes`
	from {
		opacity: 0;
	}

	to {
		opacity: 1;
	}
`;
const fadeInDown = keyframes`
	from {
		opacity: 0;
		transform: translate3d(0, -100%, 0);
	}

	to {
		opacity: 1;
		transform: translate3d(0, 0, 0);
	}
`;
const fadeInDownFar = keyframes`
	from {
		opacity: 0;
		transform: translate3d(0, -100%, 0);
	}

	to {
		opacity: 1;
		transform: translate3d(0, 0, 0);
	}
`;
const fadeInLeft = keyframes`
	from {
		opacity: 0;
		transform: translate3d(-100%, 0, 0);
	}

	to {
		opacity: 1;
		transform: translate3d(0, 0, 0);
	}
`;
const fadeInLeftFar = keyframes`
	from {
		opacity: 0;
		transform: translate3d(-2000px, 0, 0);
	}

	to {
		opacity: 1;
		transform: translate3d(0, 0, 0);
	}
`;
const fadeInRight = keyframes`
	from {
		opacity: 0;
		transform: translate3d(100%, 0, 0);
	}

	to {
		opacity: 1;
		transform: translate3d(0, 0, 0);
	}
`;
const fadeInRightFar = keyframes`
	from {
		opacity: 0;
		transform: translate3d(2000px, 0, 0);
	}

	to {
		opacity: 1;
		transform: translate3d(0, 0, 0);
	}
`;
const fadeInUp = keyframes`
	from {
		opacity: 0;
		transform: translate3d(0, 100%, 0);
	}

	to {
		opacity: 1;
		transform: translate3d(0, 0, 0);
	}
`;
const fadeInUpFar = keyframes`
	from {
		opacity: 0;
		transform: translate3d(0, 2000px, 0);
	}

	to {
		opacity: 1;
		transform: translate3d(0, 0, 0);
	}
`;
const fadeOut = keyframes`
	from {
		opacity: 1;
	}

	to {
		opacity: 0;
	}
`;
const fadeOutDown = keyframes`
	from {
		opacity: 1;
	}

	to {
		opacity: 0;
		transform: translate3d(0, 100%, 0);
	}
`;
const fadeOutDownFar = keyframes`
	from {
		opacity: 1;
	}

	to {
		opacity: 0;
		transform: translate3d(0, 2000px, 0);
	}
`;
const fadeOutLeft = keyframes`
	from {
		opacity: 1;
	}

	to {
		opacity: 0;
		transform: translate3d(-100%, 0, 0);
	}
`;
const fadeOutLeftFar = keyframes`
	from {
		opacity: 1;
	}

	to {
		opacity: 0;
		transform: translate3d(-2000px, 0, 0);
	}
`;
const fadeOutRight = keyframes`
	from {
		opacity: 1;
	}

	to {
		opacity: 0;
		transform: translate3d(100%, 0, 0);
	}
`;
const fadeOutRightFar = keyframes`
	from {
		opacity: 1;
	}

	to {
		opacity: 0;
		transform: translate3d(2000px, 0, 0);
	}
`;
const fadeOutUp = keyframes`
	from {
		opacity: 1;
	}

	to {
		opacity: 0;
		transform: translate3d(0, -100%, 0);
	}
`;
const fadeOutUpFar = keyframes`
	from {
		opacity: 1;
	}

	to {
		opacity: 0;
		transform: translate3d(0, -2000px, 0);
	}
`;
export const animations = {
  backInDown,
  backInLeft,
  backInRight,
  backInUp,
  backOutDown,
  backOutLeft,
  backOutRight,
  backOutUp,
  bounceIn,
  bounceInDown,
  bounceInLeft,
  bounceInRight,
  bounceInUp,
  bounceOut,
  bounceOutDown,
  bounceOutLeft,
  bounceOutRight,
  bounceOutUp,
  fadeIn,
  fadeInDown,
  fadeInDownFar,
  fadeInLeft,
  fadeInLeftFar,
  fadeInRight,
  fadeInRightFar,
  fadeInUp,
  fadeInUpFar,
  fadeOut,
  fadeOutDown,
  fadeOutDownFar,
  fadeOutLeft,
  fadeOutLeftFar,
  fadeOutRight,
  fadeOutRightFar,
  fadeOutUp,
  fadeOutUpFar
};